import { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Bell, CheckCircle, Users, Mail, DollarSign, CreditCard,
  FileText, Lightbulb, ClipboardList, Heart, CalendarDays, Target,
  UtensilsCrossed, Car, ShoppingBag, Clapperboard, Pill, Home,
  Zap, Smartphone, Package, MapPin, Send as SendIcon, Activity,
} from 'lucide-react';
import api from '../lib/api';
import { executeReminderOrTaskIntent, executeExpenseCorrection, executeNoteCorrection, executeWorkoutCorrection, executeMeetingNoteCorrection, isCorrectionEdit, isLikelyCorrectionUtterance } from '../lib/voiceCorrectionRouting';
import { CaptureConfirmCards } from './CaptureConfirmCards';
import { trackEvent } from '../lib/analytics';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onCaptureItems?: (items: any[]) => void;
}

type VoiceState = 'idle' | 'recording' | 'processing' | 'analyzing' | 'analyzed' | 'editing' | 'done';

function voiceSaveType(intent: unknown): string {
  const allowed = ['set_reminder', 'delegate_reminder', 'create_task', 'log_expense', 'capture_note', 'log_workout', 'add_meeting_note'];
  return typeof intent === 'string' && allowed.includes(intent) ? intent : 'other';
}

const CAT_ICON_MAP: Record<string, React.ComponentType<any>> = {
  food: UtensilsCrossed, transport: Car, shopping: ShoppingBag, entertainment: Clapperboard,
  health: Pill, family: Users, business: CreditCard, rent: Home,
  utilities: Zap, subscription: Smartphone, other: Package,
};

const INTENT_DESCRIPTIONS: Record<string, (task: string, data: any) => string> = {
  set_reminder: (task, data) => `set a reminder: "${task}"${data.suggested_due_date ? ` for ${new Date(data.suggested_due_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}` : ''}`,
  create_task: (task) => `create a task: "${task}"`,
  delegate_task: (task) => `delegate: "${task}"`,
  draft_email: (task) => `draft an email: "${task}"`,
  finance_query: (task) => `ask the CFO about: "${task}"`,
  log_expense: (task, data) => `log an expense${data.expense_amount ? `: AED ${data.expense_amount}` : ''}${data.expense_merchant ? ` at ${data.expense_merchant}` : ''}`,
  capture_note: (task) => `save this as a note: "${task}"`,
  log_workout: (task, data) => `log a workout${data.workout_activity ? `: ${data.workout_activity}` : `: "${task}"`}${data.workout_minutes ? ` (${data.workout_minutes} min)` : ''}`,
  brainstorm: (task) => `brainstorm: "${task}"`,
  action_plan: (task) => `build an action plan for: "${task}"`,
  relationship_query: (task) => `check in on: "${task}"`,
  meeting_prep: (task) => `prepare for: "${task}"`,
  ask_question: (task) => `ask AXIS: "${task}"`,
};

const INTENT_ICON_MAP: Record<string, React.ComponentType<any>> = {
  set_reminder: Bell, create_task: CheckCircle, delegate_task: Users,
  draft_email: Mail, finance_query: DollarSign, log_expense: CreditCard,
  capture_note: FileText, brainstorm: Lightbulb, action_plan: ClipboardList,
  relationship_query: Heart, meeting_prep: CalendarDays, ask_question: Target,
  log_workout: Activity,
};

export function VoiceModal({ isOpen, onClose, onCaptureItems }: Props) {
  const navigate = useNavigate();
  const [voiceState, setVoiceState] = useState<VoiceState>('idle');
  const [transcript, setTranscript] = useState('');
  const [intentData, setIntentData] = useState<any>(null);
  const [error, setError] = useState('');
  const [actionDone, setActionDone] = useState('');
  const [editForm, setEditForm] = useState({ title: '', date: '', notes: '' });
  const [showExpenseSheet, setShowExpenseSheet] = useState(false);
  const [expenseDraft, setExpenseDraft] = useState({ amount: '', category: 'food', description: '', merchant: '', payment_method: 'card' });
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const maxRecordingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // When a correction matches several recent expenses/notes, the server
  // returns the candidates and we ask the user which record to fix.
  const [correctionChoices, setCorrectionChoices] = useState<null | { kind: 'expense' | 'note' | 'workout' | 'meeting note'; candidates: any[]; data: any; draft?: any }>(null);
  const [captureItems, setCaptureItems] = useState<any[]>([]);
  const [savingAll, setSavingAll] = useState(false);
  const [savedCount, setSavedCount] = useState(0);
  const [savedItems, setSavedItems] = useState<any[]>([]);
  const [useMultiCapture, setUseMultiCapture] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setVoiceState('idle');
      setTranscript('');
      setError('');
      setActionDone('');
      setIntentData(null);
      setShowExpenseSheet(false);
      setCorrectionChoices(null);
      setCaptureItems([]);
      setSavingAll(false);
      setSavedCount(0);
      setSavedItems([]);
      setUseMultiCapture(false);
    } else {
      stopStream();
    }
  }, [isOpen]);

  const stopStream = () => {
    streamRef.current?.getTracks().forEach(t => t.stop());
    streamRef.current = null;
  };

  const handleClose = useCallback(() => {
    if (mediaRecorderRef.current?.state === 'recording') mediaRecorderRef.current.stop();
    stopStream();
    onClose();
  }, [onClose]);

  const autoAnalyze = useCallback(async (text: string) => {
    setVoiceState('analyzing');
    // Correction utterances ("no, make that 5pm") must skip the multi-capture
    // path — /capture/save-all does blind inserts and would create a duplicate.
    // detect-intent + executeReminderOrTaskIntent route them to resolve-edit.
    if (!isLikelyCorrectionUtterance(text)) {
      try {
        const { data: captureData } = await api.post('/capture/extract', {
          input: text,
          source: 'voice',
        });

        if (captureData.count > 1) {
          setCaptureItems(captureData.items);
          setUseMultiCapture(true);
          setVoiceState('analyzed');
          return;
        }

        if (captureData.count === 1 && captureData.items[0]?.confidence >= 0.85) {
          setCaptureItems(captureData.items);
          setUseMultiCapture(true);
          setVoiceState('analyzed');
          return;
        }
      } catch (err) {
        console.error('[VoiceModal] multi-capture extract failed, falling back to single-intent flow:', err);
      }
    }

    try {
      const { data } = await api.post('/ai-delegate/detect-intent', { input: text });
      let enriched = { ...data };
      if (data.intent === 'log_expense') {
        try {
          const { data: parsed } = await api.post('/expenses/voice-parse', { text });
          setExpenseDraft({
            amount: parsed.amount ? String(parsed.amount) : '',
            category: (parsed.category || 'food').toLowerCase(),
            description: parsed.description || text,
            merchant: parsed.merchant || '',
            payment_method: parsed.payment_method || 'card',
          });
          enriched = { ...data, expense_amount: parsed.amount, expense_merchant: parsed.merchant, expense_category: parsed.category };
        } catch (err) { console.error('[VoiceModal] expense voice-parse failed:', err); }
      }
      setIntentData(enriched);
      setVoiceState('analyzed');
    } catch (err) {
      console.error('[VoiceModal] intent detection failed, defaulting to ask_question:', err);
      setIntentData({ intent: 'ask_question', extracted_task: text, confidence: 0.5 });
      setVoiceState('analyzed');
    }
  }, []);

  const handleConfirmAll = useCallback(async (items: any[]) => {
    setSavingAll(true);
    try {
      const { data: result } = await api.post('/capture/save-all', {
        items: items.map((i: any) => ({ type: i.type, data: i.data })),
      });
      setSavedCount(result.success_count);
      trackEvent('voice_save_succeeded', {
        save_type: 'multi_capture',
        item_count: Number(result.success_count) || 0,
      });
      setSavedItems(items);
      if (onCaptureItems) onCaptureItems(items);
      setVoiceState('done');
      setTimeout(handleClose, 2000);
    } catch (err) {
      trackEvent('voice_save_failed', { save_type: 'multi_capture', reason: 'request_failed' });
      console.error('[VoiceModal] save-all failed:', err);
      setSavingAll(false);
    }
  }, [handleClose, onCaptureItems]);

  const handleCircleTap = useCallback(async () => {
    if (voiceState === 'processing' || voiceState === 'analyzing' || voiceState === 'analyzed' || voiceState === 'editing') return;
    if (voiceState === 'recording') { mediaRecorderRef.current?.stop(); return; }

    setError('');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: false,
          sampleRate: 16000,
        }
      });
      streamRef.current = stream;
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : MediaRecorder.isTypeSupported('audio/webm')
        ? 'audio/webm'
        : 'audio/mp4';
      const mr = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = mr;
      chunksRef.current = [];

      mr.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = async () => {
        if (maxRecordingTimerRef.current) { clearTimeout(maxRecordingTimerRef.current); maxRecordingTimerRef.current = null; }
        stopStream();
        setVoiceState('processing');
        try {
          const blob = new Blob(chunksRef.current, { type: mimeType });
          const form = new FormData();
          form.append('audio', blob, 'recording.webm');
          const { data } = await api.post('/voice/transcribe', form, { headers: { 'Content-Type': 'multipart/form-data' } });
          const text = data.text || data.transcript || '';
          if (!text) throw new Error('Empty transcript');
          setTranscript(text);
          trackEvent('voice_transcription_succeeded', { surface: 'capture_modal' });
          await autoAnalyze(text);
        } catch (err) {
          trackEvent('voice_transcription_failed', { surface: 'capture_modal', reason: 'request_failed' });
          console.error('[VoiceModal] transcription failed:', err);
          setError('Transcription failed. Please try again.');
          setVoiceState('idle');
        }
      };

      mr.start(100);
      trackEvent('voice_capture_started', { surface: 'capture_modal' });
      setVoiceState('recording');
      // Auto-stop after 120 seconds to avoid runaway recording
      maxRecordingTimerRef.current = setTimeout(() => {
        if (mediaRecorderRef.current?.state === 'recording') {
          mediaRecorderRef.current.stop();
        }
      }, 120000);
    } catch (err) {
      trackEvent('voice_capture_failed', { surface: 'capture_modal', reason: 'permission_or_device' });
      console.error('[VoiceModal] microphone access/recording setup failed:', err);
      setError('Microphone access needed — check your browser settings');
      setVoiceState('idle');
    }
  }, [voiceState, autoAnalyze]);

  const executeIntent = useCallback(async (data: any, draft: typeof expenseDraft) => {
    const { intent, extracted_task, suggested_due_date, suggested_advisor, priority, requires_delegation, delegation_type } = data;

    // Pre-empt any intent that misclassifies trip-planning / list-making asks as create_task or delegate_*.
    // These should always open AXIS chat with the full transcript so AXIS can answer with a real list.
    const tripOrListPattern = /\b(plan|planning|itinerary)\b.*\b(trip|travel|holiday|vacation|visit|going)\b|\b(make|give|build|draft|create)\s+(me\s+)?(a\s+|an\s+)?(list|recommendations?|suggestions?|ideas?|places?|restaurants?|things?\s+to\s+(do|see|eat))\b|\bwhere\s+(should|can)\s+i\b|\bwhat\s+(should|can)\s+i\s+(do|see|eat|visit)\b/i;
    if (tripOrListPattern.test(transcript)) {
      const isTrip = /\b(trip|travel|holiday|vacation|itinerary)\b/i.test(transcript);
      setActionDone(isTrip ? 'Opening AXIS to plan your trip...' : 'Opening AXIS to draft your list...');
      setTimeout(() => { handleClose(); navigate('/chat', { state: { autoSend: transcript } }); }, 1500);
      return;
    }

    switch (intent) {
      case 'set_reminder':
      case 'delegate_reminder': {
        const { message } = await executeReminderOrTaskIntent(api, { ...data, intent }, transcript);
        trackEvent('voice_save_succeeded', { save_type: voiceSaveType(intent), item_count: 1 });
        setActionDone(message);
        setTimeout(handleClose, 1500);
        break;
      }

      case 'log_expense': {
        // A correction ("no, the dinner was 500 not 450") must AMEND the
        // original expense via resolve-edit, never insert a duplicate row.
        if (isCorrectionEdit(data)) {
          const expenseDraftFields = {
            amount: draft.amount || data.expense_amount,
            category: draft.category || data.expense_category,
            merchant: draft.merchant || data.expense_merchant,
          };
          const res = await executeExpenseCorrection(api, data, expenseDraftFields);
          if (res.action === 'ambiguous' && res.candidates?.length) {
            setCorrectionChoices({ kind: 'expense', candidates: res.candidates, data, draft: expenseDraftFields });
            return;
          }
          trackEvent('voice_save_succeeded', { save_type: 'log_expense', item_count: 1 });
          setActionDone(res.message);
          setTimeout(handleClose, 2200);
          break;
        }
        if (!draft.amount) { setShowExpenseSheet(true); return; }
        await api.post('/expenses', { amount: Number(draft.amount), category: (draft.category || 'other').toLowerCase(), description: draft.description, merchant: draft.merchant || undefined, payment_method: draft.payment_method, source: 'voice' });
        trackEvent('voice_save_succeeded', { save_type: 'log_expense', item_count: 1 });
        setActionDone('Expense logged!');
        setTimeout(handleClose, 1500);
        break;
      }

      case 'create_task': {
        const { message } = await executeReminderOrTaskIntent(api, { ...data, intent }, transcript);
        trackEvent('voice_save_succeeded', { save_type: 'create_task', item_count: 1 });
        setActionDone(message);
        setTimeout(handleClose, 1500);
        break;
      }

      case 'capture_note': {
        // A correction ("actually the note should say Q3") must UPDATE the
        // original note via resolve-edit, never insert a duplicate row.
        if (isCorrectionEdit(data)) {
          const res = await executeNoteCorrection(api, data, transcript);
          if (res.action === 'ambiguous' && res.candidates?.length) {
            setCorrectionChoices({ kind: 'note', candidates: res.candidates, data });
            return;
          }
          trackEvent('voice_save_succeeded', { save_type: 'capture_note', item_count: 1 });
          setActionDone(res.message);
          setTimeout(handleClose, 2200);
          break;
        }
        await api.post('/notes', { content: extracted_task || transcript, source: 'voice' });
        trackEvent('voice_save_succeeded', { save_type: 'capture_note', item_count: 1 });
        setActionDone('Note saved!');
        setTimeout(handleClose, 1500);
        break;
      }

      case 'log_workout': {
        // A correction ("actually that run was 45 minutes not 30") must AMEND
        // the original workout via resolve-edit, never insert a duplicate row.
        if (isCorrectionEdit(data)) {
          const res = await executeWorkoutCorrection(api, data, {
            duration_minutes: data.workout_minutes ?? undefined,
            intensity: data.workout_intensity || undefined,
            activity_type: data.workout_activity || undefined,
          });
          if (res.action === 'ambiguous' && res.candidates?.length) {
            setCorrectionChoices({ kind: 'workout', candidates: res.candidates, data });
            return;
          }
          trackEvent('voice_save_succeeded', { save_type: 'log_workout', item_count: 1 });
          setActionDone(res.message);
          setTimeout(handleClose, 2200);
          break;
        }
        await api.post('/fitness/logs', {
          activity_type: data.workout_activity || 'general',
          title: extracted_task || transcript,
          duration_minutes: data.workout_minutes || null,
          intensity: data.workout_intensity || 'moderate',
          notes: extracted_task ? transcript : undefined,
          source: 'voice',
        });
        trackEvent('voice_save_succeeded', { save_type: 'log_workout', item_count: 1 });
        setActionDone('Workout logged!');
        setTimeout(handleClose, 1500);
        break;
      }

      case 'add_meeting_note': {
        // A correction ("actually the meeting note should say Q3") must
        // UPDATE the original meeting note via resolve-edit, never save a new
        // one.
        if (isCorrectionEdit(data)) {
          const res = await executeMeetingNoteCorrection(api, data, transcript);
          if (res.action === 'ambiguous' && res.candidates?.length) {
            setCorrectionChoices({ kind: 'meeting note', candidates: res.candidates, data });
            return;
          }
          trackEvent('voice_save_succeeded', { save_type: 'add_meeting_note', item_count: 1 });
          setActionDone(res.message);
          setTimeout(handleClose, 2200);
          break;
        }
        await api.post('/meetings/notes', { title: 'Meeting', raw_notes: extracted_task || transcript });
        trackEvent('voice_save_succeeded', { save_type: 'add_meeting_note', item_count: 1 });
        setActionDone('Meeting note saved!');
        setTimeout(handleClose, 1500);
        break;
      }

      case 'delegate_task':
      case 'delegate_email':
      case 'delegate_action_plan':
      case 'delegate_subtasks':
      case 'general_delegate': {
        // /delegate is just a redirect to /command and the prefill is dropped, so the user
        // would land on an empty Command screen. Route to AXIS chat with autoSend instead so
        // a real delegation draft appears (same pattern as trip/list intents above).
        const lbl = intent === 'delegate_email' ? 'Drafting email in AXIS...'
          : intent === 'delegate_subtasks' ? 'Asking AXIS to break this down...'
          : intent === 'delegate_action_plan' ? 'Asking AXIS for an action plan...'
          : 'Asking AXIS to draft this...';
        setActionDone(lbl);
        setTimeout(() => { handleClose(); navigate('/chat', { state: { autoSend: transcript } }); }, 1500);
        break;
      }

      case 'draft_email': {
        setActionDone('Drafting email in AXIS...');
        setTimeout(() => { handleClose(); navigate('/chat', { state: { autoSend: transcript } }); }, 1500);
        break;
      }

      case 'find_places':
      case 'action_plan':
      case 'brainstorm': {
        const lbl = intent === 'find_places' ? 'Opening AXIS to suggest places...'
          : intent === 'action_plan' ? 'Opening AXIS to draft your plan...'
          : 'Opening AXIS to brainstorm...';
        setActionDone(lbl);
        setTimeout(() => { handleClose(); navigate('/chat', { state: { autoSend: transcript } }); }, 1500);
        break;
      }

      case 'finance_query': {
        setActionDone('Opening Finance advisor...');
        setTimeout(() => { handleClose(); navigate('/advisor/cfo', { state: { prefill: extracted_task } }); }, 1500);
        break;
      }

      default: {
        if (requires_delegation) {
          setActionDone('Asking AXIS to draft this...');
          setTimeout(() => { handleClose(); navigate('/chat', { state: { autoSend: transcript } }); }, 1500);
        } else if (suggested_advisor && suggested_advisor !== 'null' && suggested_advisor !== 'chief_of_staff') {
          setActionDone('Routing to advisor...');
          setTimeout(() => { handleClose(); navigate(`/advisor/${suggested_advisor}`, { state: { prefill: extracted_task } }); }, 1500);
        } else {
          setActionDone('Opening AXIS chat...');
          setTimeout(() => { handleClose(); navigate('/chat', { state: { autoSend: transcript } }); }, 1500);
        }
        break;
      }
    }
  }, [transcript, navigate, handleClose]);

  // The user picked which record the correction should apply to.
  const handleCorrectionPick = useCallback(async (id: string) => {
    if (!correctionChoices) return;
    try {
      const res = correctionChoices.kind === 'expense'
        ? await executeExpenseCorrection(api, correctionChoices.data, { ...(correctionChoices.draft || {}), target_id: id })
        : correctionChoices.kind === 'workout'
        ? await executeWorkoutCorrection(api, correctionChoices.data, {
            duration_minutes: correctionChoices.data.workout_minutes ?? undefined,
            intensity: correctionChoices.data.workout_intensity || undefined,
            activity_type: correctionChoices.data.workout_activity || undefined,
            target_id: id,
          })
        : correctionChoices.kind === 'meeting note'
        ? await executeMeetingNoteCorrection(api, correctionChoices.data, transcript, { targetId: id })
        : await executeNoteCorrection(api, correctionChoices.data, transcript, { targetId: id });
      setCorrectionChoices(null);
      trackEvent('voice_save_succeeded', {
        save_type: correctionChoices.kind === 'meeting note' ? 'add_meeting_note' : correctionChoices.kind === 'note' ? 'capture_note' : `log_${correctionChoices.kind}`,
        item_count: 1,
      });
      setActionDone(res.message);
    } catch (err) {
      trackEvent('voice_save_failed', {
        save_type: correctionChoices.kind === 'meeting note' ? 'add_meeting_note' : correctionChoices.kind === 'note' ? 'capture_note' : `log_${correctionChoices.kind}`,
        reason: 'request_failed',
      });
      console.error('[VoiceModal] correction apply failed:', err);
      setCorrectionChoices(null);
      setActionDone('Could not apply the fix — nothing was changed.');
    }
    setTimeout(handleClose, 1800);
  }, [correctionChoices, transcript, handleClose]);

  const handleConfirm = useCallback(async () => {
    if (!intentData) return;
    try { await executeIntent(intentData, expenseDraft); } catch (err) {
      trackEvent('voice_save_failed', {
        save_type: voiceSaveType(intentData.intent),
        reason: 'request_failed',
      });
      console.error('[VoiceModal] intent execution failed:', err);
      setActionDone('Done');
      setTimeout(handleClose, 1200);
    }
  }, [intentData, expenseDraft, executeIntent, handleClose]);

  const handleSaveEdit = useCallback(async () => {
    try {
      if (intentData?.intent === 'set_reminder' || intentData?.intent === 'delegate_reminder') {
        let due_at: string | undefined;
        if (editForm.date) { try { due_at = new Date(editForm.date).toISOString(); } catch (err) { console.error('[VoiceModal] invalid edit date, saving without due date:', err); } }
        await api.post('/reminders', { title: editForm.title || transcript, due_at, category: 'general', source: 'voice' });
        trackEvent('voice_save_succeeded', { save_type: 'reminder', item_count: 1 });
        setActionDone('Reminder set!');
      } else if (intentData?.intent === 'log_expense') {
        await api.post('/expenses', { amount: Number(expenseDraft.amount), category: expenseDraft.category, description: expenseDraft.description, merchant: expenseDraft.merchant || undefined, payment_method: expenseDraft.payment_method, source: 'voice' });
        trackEvent('voice_save_succeeded', { save_type: 'expense', item_count: 1 });
        setActionDone('Expense logged!');
      } else {
        setActionDone('Saved!');
      }
      setTimeout(handleClose, 1500);
    } catch (err) {
      trackEvent('voice_save_failed', { save_type: 'edited_capture', reason: 'request_failed' });
      console.error('[VoiceModal] save edit failed:', err);
      setActionDone('Saved!');
      setTimeout(handleClose, 1200);
    }
  }, [editForm, intentData, expenseDraft, transcript, handleClose]);

  if (!isOpen) return null;

  const statusText = voiceState === 'idle' ? 'Tap to speak'
    : voiceState === 'recording' ? 'Listening... tap to stop'
    : voiceState === 'processing' ? 'AXIS is processing...'
    : 'Analyzing...';

  const circleAnim = voiceState === 'recording' ? 'voicePulse 1.2s ease-in-out infinite'
    : (voiceState === 'processing' || voiceState === 'analyzing') ? 'voiceSpin 1s linear infinite' : 'none';

  const intent = intentData?.intent || 'ask_question';
  const intentDesc = intentData
    ? (INTENT_DESCRIPTIONS[intent] || INTENT_DESCRIPTIONS.ask_question)(intentData.extracted_task || transcript, intentData)
    : '';
  const IntentIconComp = INTENT_ICON_MAP[intent] || Target;

  return (
    <>
      <style>{`
        @keyframes voicePulse {
          0%, 100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(232,155,48,0.4); }
          50% { transform: scale(1.06); box-shadow: 0 0 0 16px rgba(232,155,48,0); }
        }
        @keyframes voiceSpin { to { transform: rotate(360deg); } }
        @keyframes goldDots {
          0%, 80%, 100% { opacity: 0.2; transform: scale(0.8); }
          40% { opacity: 1; transform: scale(1); }
        }
      `}</style>

      <div onClick={handleClose} className="lumen-overlay" style={{ position: 'fixed', inset: 0, zIndex: 200 }} />

      <div style={{
        position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 201,
        background: 'var(--card)', borderRadius: '24px 24px 0 0',
        maxHeight: '85vh', overflowY: 'auto',
        boxShadow: 'var(--shadow)',
        WebkitOverflowScrolling: 'touch' as any,
        overscrollBehavior: 'contain' as any,
        padding: '20px 20px 40px',
      }}>
        <div style={{ width: '36px', height: '4px', background: 'var(--border)', borderRadius: '2px', margin: '0 auto 20px' }} />

        {voiceState === 'done' ? (
          <div style={{ textAlign: 'center', padding: '24px 0' }}>
            <div style={{ marginBottom: '12px', display: 'flex', justifyContent: 'center' }}><CheckCircle size={40} style={{ color: 'var(--success)' }} /></div>
            <p style={{ color: 'var(--text)', fontWeight: 700, fontSize: '18px', margin: '0 0 16px' }}>
              Saved {savedCount} item{savedCount !== 1 ? 's' : ''}
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', justifyContent: 'center' }}>
              {savedItems.map((item, i) => (
                <span key={i} style={{
                  background: 'var(--gold-dim)',
                  color: 'var(--gold)',
                  fontSize: '12px',
                  fontWeight: 600,
                  padding: '4px 10px',
                  borderRadius: '20px',
                  border: '1px solid rgba(232,155,48,0.3)',
                }}>
                  {item.icon} {item.display_label?.substring(0, 30) || item.type}
                </span>
              ))}
            </div>
          </div>
        ) : actionDone ? (
          <div style={{ textAlign: 'center', padding: '24px 0' }}>
            <p style={{ color: 'var(--gold)', fontWeight: 700, fontSize: '18px', margin: '0 0 16px' }}>{actionDone}</p>
            {transcript && (
              <p style={{ color: 'var(--muted)', fontSize: '13px', lineHeight: 1.5, margin: 0, padding: '12px', background: 'var(--bg)', borderRadius: '12px', border: '1px solid var(--border)', textAlign: 'left' }}>
                <span style={{ color: 'var(--muted)', fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, display: 'block', marginBottom: '4px' }}>You said</span>
                {transcript}
              </p>
            )}
          </div>
        ) : correctionChoices ? (
          <div>
            <p style={{ color: 'var(--text)', fontWeight: 700, fontSize: '16px', margin: '0 0 4px' }}>
              Which {correctionChoices.kind} should I fix?
            </p>
            <p style={{ color: 'var(--muted)', fontSize: '13px', margin: '0 0 14px', lineHeight: 1.4 }}>
              Several recent {correctionChoices.kind === 'expense' ? 'expenses' : correctionChoices.kind === 'workout' ? 'workouts' : correctionChoices.kind === 'meeting note' ? 'meeting notes' : 'notes'} match — pick the one to update.
            </p>
            {correctionChoices.candidates.map((c: any) => (
              <button
                key={c.id}
                onClick={() => handleCorrectionPick(c.id)}
                style={{ display: 'block', width: '100%', textAlign: 'left', background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: '12px', padding: '12px', color: 'var(--text)', fontSize: '13px', cursor: 'pointer', marginBottom: '8px', fontFamily: 'inherit' }}
              >
                {correctionChoices.kind === 'expense' ? (
                  <>
                    <span style={{ fontWeight: 700 }}>AED {c.amount}</span>
                    {' — '}{c.description || c.merchant || c.category}
                    {c.expense_date && <span style={{ color: 'var(--muted)' }}> · {c.expense_date}</span>}
                  </>
                ) : correctionChoices.kind === 'workout' ? (
                  <>
                    <span style={{ fontWeight: 700 }}>{c.title || c.activity_type || 'Workout'}</span>
                    {c.duration_minutes ? ` — ${c.duration_minutes} min` : ''}
                    {c.date && <span style={{ color: 'var(--muted)' }}> · {c.date}</span>}
                  </>
                ) : correctionChoices.kind === 'meeting note' ? (
                  <>
                    <span style={{ fontWeight: 700 }}>{c.title || 'Meeting note'}</span>
                    {c.summary ? ` — ${String(c.summary).substring(0, 70)}${String(c.summary).length > 70 ? '…' : ''}` : ''}
                  </>
                ) : (
                  <>{String(c.content || '').substring(0, 90)}{String(c.content || '').length > 90 ? '…' : ''}</>
                )}
              </button>
            ))}
            <button onClick={handleClose} style={{ width: '100%', padding: '10px', background: 'var(--bg)', color: 'var(--muted)', fontSize: '13px', border: '1px solid var(--border)', borderRadius: '10px', cursor: 'pointer', fontFamily: 'inherit' }}>Cancel — change nothing</button>
          </div>
        ) : showExpenseSheet ? (
          <div>
            <p style={{ color: 'var(--text)', fontWeight: 700, fontSize: '16px', margin: '0 0 16px' }}>Log Expense</p>
            <div style={{ textAlign: 'center', marginBottom: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                <span style={{ color: 'var(--muted)', fontSize: '20px', fontWeight: 700 }}>AED</span>
                <input type="number" value={expenseDraft.amount} onChange={e => setExpenseDraft(p => ({ ...p, amount: e.target.value }))} placeholder="0" style={{ background: 'none', border: 'none', outline: 'none', color: 'var(--text)', fontSize: '40px', fontWeight: 700, width: '120px', textAlign: 'center' }} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: '8px', overflowX: 'auto', marginBottom: '16px', paddingBottom: '4px', scrollbarWidth: 'none' }}>
              {Object.entries(CAT_ICON_MAP).map(([cat, Icon]) => (
                <button key={cat} onClick={() => setExpenseDraft(p => ({ ...p, category: cat }))} style={{ flexShrink: 0, padding: '8px 12px', borderRadius: '20px', border: `1px solid ${expenseDraft.category === cat ? 'var(--gold)' : 'var(--border)'}`, background: expenseDraft.category === cat ? 'var(--gold-dim)' : 'var(--bg)', color: 'var(--text)', fontSize: '12px', cursor: 'pointer', whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: '4px' }}><Icon size={14} /> {cat}</button>
              ))}
            </div>
            <input value={expenseDraft.merchant} onChange={e => setExpenseDraft(p => ({ ...p, merchant: e.target.value }))} placeholder="Merchant (optional)" style={{ width: '100%', background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: '10px', padding: '10px 12px', color: 'var(--text)', fontSize: '13px', outline: 'none', boxSizing: 'border-box', marginBottom: '10px', fontFamily: 'inherit' }} />
            <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
              {['cash', 'card', 'transfer'].map(m => (
                <button key={m} onClick={() => setExpenseDraft(p => ({ ...p, payment_method: m }))} style={{ flex: 1, padding: '8px', borderRadius: '10px', border: `1px solid ${expenseDraft.payment_method === m ? 'var(--gold)' : 'var(--border)'}`, background: expenseDraft.payment_method === m ? 'var(--gold-dim)' : 'var(--bg)', color: 'var(--text)', fontSize: '12px', cursor: 'pointer', textTransform: 'capitalize', fontFamily: 'inherit' }}>{m}</button>
              ))}
            </div>
            <button onClick={handleSaveEdit} style={{ width: '100%', padding: '14px', background: 'linear-gradient(135deg, var(--gold-light), var(--gold))', color: '#fff', fontWeight: 700, fontSize: '15px', border: 'none', borderRadius: 999, cursor: 'pointer', marginBottom: '8px', fontFamily: 'inherit' }}>Log Expense</button>
            <button onClick={() => setShowExpenseSheet(false)} style={{ width: '100%', padding: '10px', background: 'var(--bg)', color: 'var(--muted)', fontSize: '13px', border: '1px solid var(--border)', borderRadius: '10px', cursor: 'pointer', fontFamily: 'inherit' }}>← Back</button>
          </div>
        ) : useMultiCapture && voiceState === 'analyzed' ? (
          <div>
            <p style={{ color: 'var(--text)', fontWeight: 700, fontSize: '17px', margin: '0 0 4px' }}>
              AXIS captured {captureItems.length} thing{captureItems.length !== 1 ? 's' : ''}
            </p>
            <p style={{ color: 'var(--muted)', fontSize: '13px', fontStyle: 'italic', margin: '0 0 16px', lineHeight: 1.4 }}>
              "{transcript}"
            </p>
            <CaptureConfirmCards
              items={captureItems}
              onConfirmAll={handleConfirmAll}
              onClose={handleClose}
              saving={savingAll}
            />
            <button
              onClick={() => {
                setUseMultiCapture(false);
                if (captureItems[0]) {
                  setIntentData({ intent: 'ask_question', extracted_task: captureItems[0].display_label, confidence: 0.8 });
                }
              }}
              style={{ width: '100%', padding: '10px', background: 'none', color: 'var(--muted)', fontSize: '13px', border: 'none', cursor: 'pointer', fontFamily: 'inherit', marginTop: '4px' }}
            >
              Edit & Save individually
            </button>
          </div>
        ) : (voiceState === 'analyzed' || voiceState === 'editing') && intentData ? (
          voiceState === 'editing' ? (
            <div>
              <p style={{ color: 'var(--text)', fontWeight: 700, fontSize: '16px', margin: '0 0 16px' }}>Edit</p>
              <input value={editForm.title} onChange={e => setEditForm(p => ({ ...p, title: e.target.value }))} placeholder="Title" style={{ width: '100%', background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: '10px', padding: '10px 12px', color: 'var(--text)', fontSize: '14px', outline: 'none', boxSizing: 'border-box', marginBottom: '10px', fontFamily: 'inherit' }} />
              {(intentData?.intent === 'set_reminder' || intentData?.intent === 'delegate_reminder') && (
                <input type="date" value={editForm.date} onChange={e => setEditForm(p => ({ ...p, date: e.target.value }))} style={{ width: '100%', background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: '10px', padding: '10px 12px', color: 'var(--text)', fontSize: '14px', outline: 'none', boxSizing: 'border-box', marginBottom: '10px', fontFamily: 'inherit' }} />
              )}
              <textarea value={editForm.notes} onChange={e => setEditForm(p => ({ ...p, notes: e.target.value }))} placeholder="Notes (optional)" rows={3} style={{ width: '100%', background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: '10px', padding: '10px 12px', color: 'var(--text)', fontSize: '14px', outline: 'none', boxSizing: 'border-box', resize: 'none', fontFamily: 'inherit', marginBottom: '16px' }} />
              <button onClick={handleSaveEdit} style={{ width: '100%', padding: '14px', background: 'linear-gradient(135deg, var(--gold-light), var(--gold))', color: '#fff', fontWeight: 700, fontSize: '15px', border: 'none', borderRadius: 999, cursor: 'pointer', marginBottom: '8px', fontFamily: 'inherit' }}>Save</button>
              <button onClick={() => setVoiceState('analyzed')} style={{ width: '100%', padding: '10px', background: 'var(--bg)', color: 'var(--muted)', fontSize: '13px', border: '1px solid var(--border)', borderRadius: '10px', cursor: 'pointer', fontFamily: 'inherit' }}>← Back</button>
            </div>
          ) : (intent === 'ask_question' || intent === 'chief_of_staff' || (intentData?.suggested_advisor === 'chief_of_staff' && !['log_expense','set_reminder','delegate_reminder','delegate_task','delegate_email','delegate_action_plan','delegate_subtasks','general_delegate','draft_email','find_places','action_plan','brainstorm','finance_query','add_meeting_note','add_decision','log_income','create_task','add_note'].includes(intent))) ? (
            <div>
              <p style={{ color: 'var(--muted)', fontSize: '12px', marginBottom: '8px', textAlign: 'center' }}>You said</p>
              <p style={{ color: 'var(--text)', fontSize: '14px', lineHeight: 1.5, marginBottom: '16px', padding: '12px', background: 'var(--bg)', borderRadius: '12px', border: '1px solid var(--border)' }}>{transcript}</p>
              <div style={{ background: 'var(--gold-dim)', border: '1px solid rgba(232,155,48,0.25)', borderRadius: '16px', padding: '16px', marginBottom: '16px' }}>
                <p style={{ color: 'var(--text)', fontSize: '14px', fontWeight: 600, margin: '0 0 4px', lineHeight: 1.4 }}>
                  {(intentData?.confidence ?? 1) < 0.7 ? "I didn't catch a clear action." : 'Open this in AXIS chat?'}
                </p>
                <p style={{ color: 'var(--muted)', fontSize: '13px', margin: 0, lineHeight: 1.4 }}>
                  {(intentData?.confidence ?? 1) < 0.7
                    ? 'Want me to send this to AXIS chat so we can sort it out together?'
                    : 'AXIS will answer this in chat.'}
                </p>
              </div>
              <button
                onClick={() => {
                  setActionDone('Opening AXIS chat...');
                  setTimeout(() => { handleClose(); navigate('/chat', { state: { autoSend: transcript } }); }, 1500);
                }}
                style={{ width: '100%', padding: '14px', background: 'linear-gradient(135deg, var(--gold-light), var(--gold))', color: '#fff', fontWeight: 700, fontSize: '15px', border: 'none', borderRadius: 999, cursor: 'pointer', marginBottom: '8px', fontFamily: 'inherit' }}
              >
                Open in AXIS chat
              </button>
              <button onClick={handleClose} style={{ width: '100%', padding: '10px', background: 'var(--bg)', color: 'var(--muted)', fontSize: '13px', border: '1px solid var(--border)', borderRadius: '10px', cursor: 'pointer', fontFamily: 'inherit' }}>Cancel</button>
            </div>
          ) : (
            <div>
              <p style={{ color: 'var(--muted)', fontSize: '12px', marginBottom: '8px', textAlign: 'center' }}>You said</p>
              <p style={{ color: 'var(--text)', fontSize: '14px', lineHeight: 1.5, marginBottom: '16px', padding: '12px', background: 'var(--bg)', borderRadius: '12px', border: '1px solid var(--border)' }}>{transcript}</p>
              <div style={{ background: 'var(--gold-dim)', border: '1px solid rgba(232,155,48,0.25)', borderRadius: '16px', padding: '16px', marginBottom: '16px' }}>
                <p style={{ color: 'var(--muted)', fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, margin: '0 0 8px' }}>AXIS will</p>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                  <IntentIconComp size={24} style={{ color: 'var(--gold)', flexShrink: 0 }} />
                  <p style={{ color: 'var(--text)', fontSize: '15px', fontWeight: 600, margin: 0, lineHeight: 1.4 }}>{intentDesc}</p>
                </div>
                {intent === 'log_expense' && expenseDraft.amount && (
                  <div style={{ marginTop: '10px', display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                    <span style={{ background: 'rgba(16,185,129,0.15)', color: 'var(--success)', fontSize: '12px', fontWeight: 600, padding: '3px 10px', borderRadius: '20px' }}>AED {expenseDraft.amount}</span>
                    <span style={{ background: 'var(--border)', color: 'var(--text)', fontSize: '12px', padding: '3px 10px', borderRadius: '20px' }}>{expenseDraft.category}</span>
                    {expenseDraft.merchant && <span style={{ background: 'var(--border)', color: 'var(--muted)', fontSize: '12px', padding: '3px 10px', borderRadius: '20px' }}>{expenseDraft.merchant}</span>}
                  </div>
                )}
              </div>
              <button onClick={handleConfirm} style={{ width: '100%', padding: '14px', background: 'linear-gradient(135deg, var(--gold-light), var(--gold))', color: '#fff', fontWeight: 700, fontSize: '15px', border: 'none', borderRadius: 999, cursor: 'pointer', marginBottom: '8px', fontFamily: 'inherit' }}>Confirm</button>
              <button onClick={() => {
                setEditForm({ title: intentData.extracted_task || transcript, date: intentData.suggested_due_date || '', notes: '' });
                if (intent === 'log_expense') setShowExpenseSheet(true);
                else setVoiceState('editing');
              }} style={{ width: '100%', padding: '10px', background: 'var(--bg)', color: 'var(--muted)', fontSize: '13px', border: '1px solid var(--border)', borderRadius: '10px', cursor: 'pointer', fontFamily: 'inherit' }}>Edit</button>
            </div>
          )
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '8px 0' }}>
            <button onClick={handleCircleTap} style={{ width: '80px', height: '80px', borderRadius: '50%', border: '2px solid var(--gold)', background: voiceState === 'recording' ? 'var(--gold-dim)' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: (voiceState === 'processing' || voiceState === 'analyzing') ? 'default' : 'pointer', animation: circleAnim, marginBottom: '16px', outline: 'none' }}>
              {(voiceState === 'processing' || voiceState === 'analyzing') ? (
                <div style={{ width: '28px', height: '28px', borderRadius: '50%', border: '2px solid var(--gold)', borderTopColor: 'transparent', animation: 'voiceSpin 0.8s linear infinite' }} />
              ) : (
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="var(--gold)" strokeWidth="1.5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4M12 3a4 4 0 014 4v4a4 4 0 01-8 0V7a4 4 0 014-4z" />
                </svg>
              )}
            </button>
            <p style={{ color: (voiceState === 'recording' || voiceState === 'analyzing') ? 'var(--gold)' : 'var(--muted)', fontSize: '14px', fontWeight: 500, marginBottom: '6px' }}>{statusText}</p>
            {(voiceState === 'analyzing') && (
              <div style={{ display: 'flex', gap: '4px', marginTop: '4px' }}>
                {[0, 1, 2].map(i => (
                  <div key={i} className="lumen-orb" style={{ width: '6px', height: '6px', animationDelay: `${i * 0.15}s` }} />
                ))}
              </div>
            )}
            {error && <p style={{ color: 'var(--danger)', fontSize: '12px', textAlign: 'center', marginTop: '8px', maxWidth: '240px' }}>{error}</p>}
            <button onClick={handleClose} style={{ marginTop: '20px', background: 'none', border: 'none', color: 'var(--muted)', fontSize: '13px', cursor: 'pointer', fontFamily: 'inherit' }}>Cancel</button>
          </div>
        )}
      </div>
    </>
  );
}

export default VoiceModal;
